import { FormControlLabel, Grid2 as Grid, MenuItem, Switch, TextField } from '@mui/material';

import type { ProductCondition, ProductUnit } from '../../api/business/types';
import { formatMoney } from '../../components/format';
import { parseMoneyInput, parseUnitQuantity } from '../../components/parse';
import { unitPriceOf } from '../../components/prices';
import { PRODUCT_CONDITIONS, PRODUCT_UNITS } from '../../components/productListing';
import type { DraftSetter, ProductDraft } from '../../components/productDraft';
import { useLanguage } from '../../i18n/LanguageContext';

type ProductLegalFieldsProps = {
  draft: ProductDraft;
  set: DraftSetter;
  currency: string;
};

/**
 * The EU and legal properties on the product form (R0030 WP-07): the condition and whether a shop prints it; the
 * unit price — unit and quantity in the pack, the price per unit computed as it is typed; the eco-tax, which is a
 * part of the price.
 */
export const ProductLegalFields = ({ draft, set, currency }: ProductLegalFieldsProps) => {
  const { language, t } = useLanguage();
  const price = parseMoneyInput(draft.price);
  const quantity = parseUnitQuantity(draft.unitQuantity);
  const unitPrice =
    draft.unitLabel === '' || price === undefined || quantity === undefined
      ? null
      : unitPriceOf(price, quantity);

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          select
          fullWidth
          label={t('legal.condition')}
          value={draft.condition}
          onChange={(e) => set('condition', e.target.value as ProductCondition)}
        >
          {PRODUCT_CONDITIONS.map((condition) => (
            <MenuItem key={condition} value={condition}>
              {t(`condition.${condition}`)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControlLabel
          control={
            <Switch checked={draft.showCondition} onChange={(e) => set('showCondition', e.target.checked)} />
          }
          label={t('legal.showCondition')}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          select
          fullWidth
          label={t('legal.unitLabel')}
          helperText={t('legal.unitPriceHint')}
          value={draft.unitLabel}
          onChange={(e) => set('unitLabel', e.target.value as ProductUnit | '')}
        >
          <MenuItem value="">{t('legal.noUnit')}</MenuItem>
          {PRODUCT_UNITS.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {t(`unit.${unit}`)}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label={t('legal.unitQuantity')}
          helperText={t('legal.unitQuantityHint')}
          value={draft.unitQuantity}
          disabled={draft.unitLabel === '' && draft.unitQuantity === ''}
          inputProps={{ inputMode: 'decimal' }}
          onChange={(e) => set('unitQuantity', e.target.value)}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <TextField
          fullWidth
          label={t('legal.unitPrice')}
          value={
            unitPrice === null || draft.unitLabel === ''
              ? ''
              : t('legal.unitPriceValue', {
                  price: formatMoney(unitPrice, currency, language),
                  unit: t(`unitSymbol.${draft.unitLabel}`),
                })
          }
          placeholder={t('legal.unitPriceNone')}
          helperText={unitPrice === null ? t('legal.unitPriceNone') : undefined}
          InputProps={{ readOnly: true }}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          fullWidth
          label={t('legal.ecotax', { currency })}
          helperText={t('legal.ecotaxHint')}
          value={draft.ecotax}
          inputProps={{ inputMode: 'decimal' }}
          onChange={(e) => set('ecotax', e.target.value)}
        />
      </Grid>
    </Grid>
  );
};
